
export interface iStorageService<T> {
    get<K extends keyof T>(key: K): T[K] | undefined;

    set<K extends keyof T>(key: K, value: T[K]): void;

    remove<K extends keyof T>(key: K): void;

    clear(): void;
}

export class StorageService<T> implements iStorageService<T> {
    constructor(private readonly storage: Storage) { }

    set<K extends keyof T>(key: K, value: T[K]): void {
        this.storage.setItem(key.toString(), JSON.stringify(value));
    }

    get<K extends keyof T>(key: K): T[K] | undefined {
        const value = this.storage.getItem(key.toString());

        if (value === null || value === 'null'
            || value === undefined || value === 'undefined') {
            return undefined;
        }

        return JSON.parse(value);
    }
    remove<K extends keyof T>(key: K): void {
        this.storage.removeItem(key.toString());
    }

    clear(): void {
        this.storage.clear();
    }
}

// Remove Session Storage at logout
export const clearAllSessionStorage = () => {
    Object.values(ResultSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(FeedbackSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(UserSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(OrganisationSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(BatchSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(DeviceSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(SkuSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
    Object.values(AuditSessionStorageKey).forEach(key => {
        sessionStorage.removeItem(key);
    });
};


export enum ResultSessionStorageKey {
    sorting = 'result_sorting',
    columnVisibility = 'result_columnVisibility',
    pagination = 'result_pagination',
    columnFilters = 'result_columnFilters',
    search = 'result_search',
    showSearch = 'result_showSearch',
    data = 'results_data',
    count = 'result_count',
    filterCriteria = 'result_filter_criteria',
    filters = 'result_filters',
    currentQueryString = 'result_current_query_string'
}

export enum FeedbackSessionStorageKey {
    sorting = 'feedback_sorting',
    columnVisibility = 'feedback_columnVisibility',
    pagination = 'feedback_pagination',
    columnFilters = 'feedback_columnFilters',
    search = 'feedback_search',
    showSearch = 'feedback_showSearch',
    data = 'feedback_data',
    count = 'feedback_count',
    filterCriteria = 'feedback_filter_criteria',
    filters = 'feedback_filters',
    currentQueryString = 'feedback_current_query_string'
}

export enum UserSessionStorageKey {
    sorting = 'user_sorting',
    columnVisibility = 'user_columnVisibility',
    pagination = 'user_pagination',
    columnFilters = 'user_columnFilters',
    search = 'user_search',
    showSearch = 'user_showSearch',
    data = 'user_data',
    count = 'user_count',
    filterCriteria = 'user_filter_criteria',
    filters = 'user_filters',
    currentQueryString = 'user_current_query_string'
}

export enum OrganisationSessionStorageKey {
    sorting = 'organisation_sorting',
    columnVisibility = 'organisation_columnVisibility',
    pagination = 'organisation_pagination',
    columnFilters = 'organisation_columnFilters',
    search = 'organisation_search',
    showSearch = 'organisation_showSearch',
    data = 'organisation_data',
    count = 'organisation_count',
    filterCriteria = 'organisation_filter_criteria',
    filters = 'organisation_filters',
    currentQueryString = 'organisation_current_query_string'

}

export enum BatchSessionStorageKey {
    sorting = 'batch_sorting',
    columnVisibility = 'batch_columnVisibility',
    pagination = 'batch_pagination',
    columnFilters = 'batch_columnFilters',
    search = 'batch_search',
    showSearch = 'batch_showSearch',
    data = 'batch_data',
    count = 'batch_count',
    filterCriteria = 'batch_filter_criteria',
    filters = 'batch_filters',
    currentQueryString = 'batch_current_query_string'
}

export enum DeviceSessionStorageKey {
    sorting = 'device_sorting',
    columnVisibility = 'device_columnVisibility',
    pagination = 'device_pagination',
    columnFilters = 'device_columnFilters',
    search = 'device_search',
    showSearch = 'device_showSearch',
    data = 'device_data',
    count = 'device_count',
    filterCriteria = 'device_filter_criteria',
    filters = 'device_filters',
    currentQueryString = 'device_current_query_string'
}

export enum SkuSessionStorageKey {
    sorting = 'sku_sorting',
    columnVisibility = 'sku_columnVisibility',
    pagination = 'sku_pagination',
    columnFilters = 'sku_columnFilters',
    search = 'sku_search',
    showSearch = 'sku_showSearch',
    data = 'sku_data',
    count = 'sku_count',
    filterCriteria = 'sku_filter_criteria',
    filters = 'sku_filters',
    currentQueryString = 'sku_current_query_string'
}

export enum LatestDCMSessionStorageKey {
    sorting = 'latestdcm_sorting',
    columnVisibility = 'latestdcm_columnVisibility',
    pagination = 'latestdcm_pagination',
    columnFilters = 'latestdcm_columnFilters',
    search = 'latestdcm_search',
    showSearch = 'latestdcm_showSearch',
    data = 'latestdcm_data',
    count = 'latestdcm_count',
    filterCriteria = 'latestdcm_filter_criteria',
    filters = 'latestdcm_filters',
    currentQueryString = 'latestdcm_current_query_string'
}

export enum AuditSessionStorageKey {
    sorting = 'audit_sorting',
    columnVisibility = 'audit_columnVisibility',
    pagination = 'audit_pagination',
    columnFilters = 'audit_columnFilters',
    search = 'audit_search',
    showSearch = 'audit_showSearch',
    data = 'audit_data',
    count = 'audit_count',
    filterCriteria = 'audit_filter_criteria',
    filters = 'audit_filters',
    currentQueryString = 'audit_current_query_string'
}


export interface ResultSessionStorageModel {
    [ResultSessionStorageKey.sorting]: any;
    [ResultSessionStorageKey.columnVisibility]: any;
    [ResultSessionStorageKey.pagination]: any;
    [ResultSessionStorageKey.columnFilters]: any;
    [ResultSessionStorageKey.search]: any;
    [ResultSessionStorageKey.showSearch]: any;
    [ResultSessionStorageKey.data]: any;
    [ResultSessionStorageKey.count]: any;
    [ResultSessionStorageKey.filterCriteria]: any;
    [ResultSessionStorageKey.filters]: any;
    [ResultSessionStorageKey.currentQueryString]: any;
}

export interface FeedbackSessionStorageModel {
    [FeedbackSessionStorageKey.sorting]: any;
    [FeedbackSessionStorageKey.columnVisibility]: any;
    [FeedbackSessionStorageKey.pagination]: any;
    [FeedbackSessionStorageKey.columnFilters]: any;
    [FeedbackSessionStorageKey.search]: any;
    [FeedbackSessionStorageKey.showSearch]: any;
    [FeedbackSessionStorageKey.data]: any;
    [FeedbackSessionStorageKey.count]: any;
    [FeedbackSessionStorageKey.filterCriteria]: any;
    [FeedbackSessionStorageKey.filters]: any;
    [FeedbackSessionStorageKey.currentQueryString]: any;
}

export interface UserSessionStorageModel {
    [UserSessionStorageKey.sorting]: any;
    [UserSessionStorageKey.columnVisibility]: any;
    [UserSessionStorageKey.pagination]: any;
    [UserSessionStorageKey.columnFilters]: any;
    [UserSessionStorageKey.search]: any;
    [UserSessionStorageKey.showSearch]: any;
    [UserSessionStorageKey.data]: any;
    [UserSessionStorageKey.count]: any;
    [UserSessionStorageKey.filterCriteria]: any;
    [UserSessionStorageKey.filters]: any;
    [UserSessionStorageKey.currentQueryString]: any;
}

export interface OrganisationSessionStorageModel {
    [OrganisationSessionStorageKey.sorting]: any;
    [OrganisationSessionStorageKey.columnVisibility]: any;
    [OrganisationSessionStorageKey.pagination]: any;
    [OrganisationSessionStorageKey.columnFilters]: any;
    [OrganisationSessionStorageKey.search]: any;
    [OrganisationSessionStorageKey.showSearch]: any;
    [OrganisationSessionStorageKey.data]: any;
    [OrganisationSessionStorageKey.count]: any;
    [OrganisationSessionStorageKey.filterCriteria]: any;
    [OrganisationSessionStorageKey.filters]: any;
    [OrganisationSessionStorageKey.currentQueryString]: any;
}

export interface BatchSessionStorageModel {
    [BatchSessionStorageKey.sorting]: any;
    [BatchSessionStorageKey.columnVisibility]: any;
    [BatchSessionStorageKey.pagination]: any;
    [BatchSessionStorageKey.columnFilters]: any;
    [BatchSessionStorageKey.search]: any;
    [BatchSessionStorageKey.showSearch]: any;
    [BatchSessionStorageKey.data]: any;
    [BatchSessionStorageKey.count]: any;
    [BatchSessionStorageKey.filterCriteria]: any;
    [BatchSessionStorageKey.filters]: any;
    [BatchSessionStorageKey.currentQueryString]: any;
}

export interface DeviceSessionStorageModel {
    [DeviceSessionStorageKey.sorting]: any;
    [DeviceSessionStorageKey.columnVisibility]: any;
    [DeviceSessionStorageKey.pagination]: any;
    [DeviceSessionStorageKey.columnFilters]: any;
    [DeviceSessionStorageKey.search]: any;
    [DeviceSessionStorageKey.showSearch]: any;
    [DeviceSessionStorageKey.data]: any;
    [DeviceSessionStorageKey.count]: any;
    [DeviceSessionStorageKey.filterCriteria]: any;
    [DeviceSessionStorageKey.filters]: any;
    [DeviceSessionStorageKey.currentQueryString]: any;
}

export interface SkuSessionStorageModel {
    [SkuSessionStorageKey.sorting]: any;
    [SkuSessionStorageKey.columnVisibility]: any;
    [SkuSessionStorageKey.pagination]: any;
    [SkuSessionStorageKey.columnFilters]: any;
    [SkuSessionStorageKey.search]: any;
    [SkuSessionStorageKey.showSearch]: any;
    [SkuSessionStorageKey.data]: any;
    [SkuSessionStorageKey.count]: any;
    [SkuSessionStorageKey.filterCriteria]: any;
    [SkuSessionStorageKey.filters]: any;
    [SkuSessionStorageKey.currentQueryString]: any;
}


export interface AuditSessionStorageModel {
    [AuditSessionStorageKey.sorting]: any;
    [AuditSessionStorageKey.columnVisibility]: any;
    [AuditSessionStorageKey.pagination]: any;
    [AuditSessionStorageKey.columnFilters]: any;
    [AuditSessionStorageKey.search]: any;
    [AuditSessionStorageKey.showSearch]: any;
    [AuditSessionStorageKey.data]: any;
    [AuditSessionStorageKey.count]: any;
    [AuditSessionStorageKey.filterCriteria]: any;
    [AuditSessionStorageKey.filters]: any;
    [AuditSessionStorageKey.currentQueryString]: any;
}



export interface LatestDCMSessionStorageModel {
    [LatestDCMSessionStorageKey.data]: any;
}

export const ResultTableParameterStorageService = new StorageService<ResultSessionStorageModel>(
    sessionStorage
);

export const FeedbackTableParameterStorageService = new StorageService<FeedbackSessionStorageModel>(
    sessionStorage
);

export const UserTableParameterStorageService = new StorageService<UserSessionStorageModel>(
    sessionStorage
);

export const OrganisationTableParameterStorageService = new StorageService<OrganisationSessionStorageModel>(
    sessionStorage
);

export const BatchTableParameterStorageService = new StorageService<BatchSessionStorageModel>(
    sessionStorage
);

export const DeviceTableParameterStorageService = new StorageService<DeviceSessionStorageModel>(
    sessionStorage
);

export const SkuTableParameterStorageService = new StorageService<SkuSessionStorageModel>(
    sessionStorage
);

export const LatestDCMTableParameterStorageService = new StorageService<LatestDCMSessionStorageModel>(
    sessionStorage
);

export const AuditTableParameterStorageService = new StorageService<AuditSessionStorageModel>(
    sessionStorage
);